/**
 * Client-side mirror of the server's pricing model.
 *
 * **The server is the authority.** `server/src/shared/price-calculator.ts`:
 *
 * ```ts
 * getFee(price) = max( price / (1 - SERVICE_FEE_RATE) * SERVICE_FEE_RATE, MINIMUM_FEE ) + FEEDBACK_INCENTIVE
 * calculateSiteDayPrice: value = price + getFee(price)     // the guest is charged this
 * ```
 *
 * So the **host's listed price is their take-home**, and the fee is added ON TOP
 * of it for the guest. `objects/src/classes/price-display.ts`'s `calculateHostNet()`
 * (`value - discount - fee`) resolves back to exactly the listed price.
 *
 * This file exists only because the wizard has to quote a guest price *before
 * publishing*, when there is no site id and `PriceCalculationService` cannot be
 * called. It is a deliberate duplicate, and `pricing.util.spec.ts` pins it against
 * the same figures `server/src/shared/price-calculator.spec.ts` asserts, so a
 * divergence fails loudly rather than silently.
 *
 * Three things that are easy to get wrong, and each of which the previous version
 * of this file got wrong:
 *
 * 1. **The 15% is charged on the grossed-up total, not on the host's price.**
 *    `price / (1 - 0.15) * 0.15` is ≈17.6% of what the host keeps. Taking 15% of
 *    the host's price gives a different, smaller number.
 * 2. **Per night, then sum.** Both the percentage and the $5 floor apply per night
 *    — the server computes each date separately. A stay is
 *    `nights × (price + getFee(price))`, never `getFee(price × nights)`.
 * 3. **Two decimal places.** The server's `PriceComponent` rounds `value` and `fee`
 *    with `toPrecision(v, 2)`. Rounding to whole dollars here would disagree with
 *    the booking widget by cents on most prices.
 *
 * NOT modelled here, deliberately: `listing.weekdayDiscount` and the server's
 * hardcoded holiday table, both of which vary the price per date. The wizard only
 * has a flat nightly price, so what it quotes is the undiscounted rate.
 */

/** Matches SERVICE_FEE_RATE in server/src/shared/constants.ts. */
export const SERVICE_FEE_RATE = 0.15;
/** Matches MINIMUM_FEE in server/src/shared/constants.ts. */
export const MINIMUM_FEE_PER_NIGHT = 5;
/** Matches FEEDBACK_INCENTIVE in server/src/shared/constants.ts. */
export const FEEDBACK_INCENTIVE_PER_NIGHT = 5;

/** The server's `toPrecision(value, 2)` (helpers/src/helpers/math.ts), inlined so
 *  this lib does not take a dependency on the legacy `@curbnturf/helpers` package. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Per-night fee added on top of the host's price — the mirror of the server's
 * `getFee()`. Includes both the service fee (the greater of 15% of the grossed-up
 * total or $5) and the $5 feedback incentive, because the server returns them as
 * one figure and the two are added together in the same expression.
 */
export function computeGuestFeePerNight(hostPrice: number): number {
  const safe = Math.max(0, hostPrice);
  const percent = (safe / (1 - SERVICE_FEE_RATE)) * SERVICE_FEE_RATE;
  return round2(Math.max(percent, MINIMUM_FEE_PER_NIGHT) + FEEDBACK_INCENTIVE_PER_NIGHT);
}

/** True when the $5 floor is the binding constraint rather than the percentage. */
export function guestFeeIsAtFloor(hostPrice: number): boolean {
  const safe = Math.max(0, hostPrice);
  return (safe / (1 - SERVICE_FEE_RATE)) * SERVICE_FEE_RATE < MINIMUM_FEE_PER_NIGHT;
}

/** What one night costs the guest: the host's price plus the fee on top. */
export function computeGuestPricePerNight(hostPrice: number): number {
  return round2(Math.max(0, hostPrice) + computeGuestFeePerNight(hostPrice));
}

/**
 * What the guest pays for an add-on (per unit, night or person), off the host's
 * price — the mirror of `PriceCalculationService`'s extras branch
 * (server/src/reservation/price-calculation.service.ts): the same 15%-of-gross
 * fee as a night, but no $5 floor and no feedback incentive.
 */
export function computeGuestExtraPrice(hostPrice: number): number {
  return round2(Math.max(0, hostPrice) / (1 - SERVICE_FEE_RATE));
}

/**
 * Inverse of `computeGuestPricePerNight`: the host price that makes the server
 * charge `guestPrice` per night. Lets the wizard take the price a host wants
 * guests to SEE and store the take-home the server actually expects.
 *
 * Mirrors curbnturf.com's `PriceComponent.totalPriceChange`
 * (libs/host-forms/src/lib/price/price.component.ts):
 *
 *   hostPrice = G - max((G - incentive) × rate, floor) - incentive
 *
 * which is the algebraic inverse of the server's
 * `p + max(p / (1 - rate) × rate, floor) + incentive`. Round-trips exactly for
 * every whole dollar; on some half-dollars the server's own 2dp rounding lands a
 * cent off (e.g. $60.50 -> $60.51), so callers should re-display
 * `computeGuestPricePerNight(result)` rather than echo what was typed.
 *
 * Below the minimum (floor + incentive = $10) no host price can produce the
 * figure, so returns 0.
 */
export function computeHostPriceFromGuestPrice(guestPrice: number): number {
  const min = MINIMUM_FEE_PER_NIGHT + FEEDBACK_INCENTIVE_PER_NIGHT;
  if (!(guestPrice >= min)) return 0;
  const fee = Math.max((guestPrice - FEEDBACK_INCENTIVE_PER_NIGHT) * SERVICE_FEE_RATE, MINIMUM_FEE_PER_NIGHT);
  return round2(guestPrice - fee - FEEDBACK_INCENTIVE_PER_NIGHT);
}

/**
 * What a whole stay costs the guest. Computed per night and then multiplied,
 * which is what the server does by pricing each date separately — see note 2 in
 * the file header.
 */
export function computeGuestPrice(hostPrice: number, nights: number): number {
  if (nights <= 0) return 0;
  return round2(computeGuestPricePerNight(hostPrice) * nights);
}

/** Total feedback incentive charged for a stay of `nights` nights. Refunded to the
 *  guest as CurbNTurf Cash when they leave a qualifying review. */
export function computeFeedbackIncentive(nights: number): number {
  return Math.max(0, nights) * FEEDBACK_INCENTIVE_PER_NIGHT;
}

/** Whole nights between two dates, clamped to `floor` (defaults to 1 for a minimum 1-night stay). */
export function getNights(start: Date, end: Date, floor: 0 | 1 = 1): number {
  return Math.max(floor, Math.round((end.getTime() - start.getTime()) / 86_400_000));
}

/** Formats a number as a whole-dollar USD currency string, e.g. `$1,234`. */
export function formatCurrencyUSD(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}
